import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHelper } from '@umss/shared/utils';
import { Observable } from 'rxjs';
import {
  CreateFacilityCorporateLinkRequestDto,
  RejectFacilityCorporateLinkRequestDto,
  FacilityCorporateLinkRequestDto,
  FacilityCorporateLinkRequestFilterDto,
  UnaffiliatedFacilitiesFilterDto,
  AvailableCorporationsFilterDto,
  FacilityListItemDto,
  CorporationListItemDto,
  PagedResultDto
} from '../../models';

/**
 * Service for managing facility-corporate link requests.
 * Handles the creation, approval, rejection, and querying of link requests
 * between facilities and corporations.
 */
@Injectable({ providedIn: 'root' })
export class FacilityCorporateLinkRequestService {
  private readonly baseUrl = '/api/facility-corporate-link-requests';
  private readonly http = inject(HttpClient);

  // ============================================
  // PICKERS (for UI selection)
  // ============================================

  /**
   * GET /unaffiliated-facilities
   * Lists facilities where CorporationId IS NULL. Used by corporate-side pickers.
   * Auth: CorporateStaff, BusinessAdministrator, SuperAdmin
   */
  getUnaffiliatedFacilities(
    filters?: UnaffiliatedFacilitiesFilterDto
  ): Observable<PagedResultDto<FacilityListItemDto>> {
    const params = HttpHelper.toHttpParams(filters ?? {});
    return this.http.get<PagedResultDto<FacilityListItemDto>>(
      `${this.baseUrl}/unaffiliated-facilities`,
      { params }
    );
  }

  /**
   * GET /available-corporations
   * Lists corporations a facility-side user can target. Used by facility-side pickers.
   * Auth: any role allowed to create link request
   */
  getAvailableCorporations(
    filters?: AvailableCorporationsFilterDto
  ): Observable<PagedResultDto<CorporationListItemDto>> {
    const params = HttpHelper.toHttpParams(filters ?? {});
    return this.http.get<PagedResultDto<CorporationListItemDto>>(
      `${this.baseUrl}/available-corporations`,
      { params }
    );
  }

  // ============================================
  // CRUD OPERATIONS
  // ============================================

  /**
   * POST /
   * Creates a new link request between a facility and a corporation.
   * The initiator (facility or corporate) is inferred server-side from the caller's role.
   *
   * Returns:
   * - 201: Request created successfully
   * - 409: Already a pending request for this facility
   * - 400: Facility is already linked to some corporation
   * - 404: Either facility or corporation doesn't exist
   *
   * Auth: FacilityAdmin, ShiftAdministrator, CorporateStaff, BusinessAdministrator, SuperAdmin
   */
  createLinkRequest(
    body: CreateFacilityCorporateLinkRequestDto
  ): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.post<FacilityCorporateLinkRequestDto>(this.baseUrl, body);
  }

  /**
   * GET /
   * Lists link requests with optional filtering by status, facilityId, corporationId.
   * Supports pagination.
   *
   * Auth: BusinessAdministrator, SuperAdmin
   */
  listLinkRequests(
    filters?: FacilityCorporateLinkRequestFilterDto
  ): Observable<PagedResultDto<FacilityCorporateLinkRequestDto>> {
    const params = HttpHelper.toHttpParams(filters);
    return this.http.get<PagedResultDto<FacilityCorporateLinkRequestDto>>(
      this.baseUrl,
      { params }
    );
  }

  /**
   * GET /{id}
   * Retrieves a single link request with related entities (facility, corporation, users).
   *
   * Auth: BusinessAdministrator, SuperAdmin
   */
  getLinkRequest(id: string): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.get<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/${id}`
    );
  }

  // ============================================
  // ACTIONS (approval/rejection)
  // ============================================

  /**
   * POST /{id}/approve
   * Approves a pending link request. Flips status to Approved and sets Facility.CorporationId.
   * Stamps decided_by and decided_at fields.
   *
   * Returns:
   * - 200: Request approved successfully
   * - 400: Request is not pending
   * - 409: Facility raced into being linked to a different corporation
   *
   * Auth: BusinessAdministrator, SuperAdmin
   */
  approveLinkRequest(id: string): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.post<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/${id}/approve`,
      {}
    );
  }

  /**
   * POST /{id}/reject
   * Rejects a pending link request.
   *
   * Returns:
   * - 200: Request rejected successfully
   * - 400: Request is not pending OR rejectionReason is empty/whitespace
   *
   * Auth: BusinessAdministrator, SuperAdmin
   */
  rejectLinkRequest(
    id: string,
    body: RejectFacilityCorporateLinkRequestDto
  ): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.post<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/${id}/reject`,
      body
    );
  }
}
